import { FC, useCallback } from 'react'
import {
  Asset,
  AttachmentReducerAction,
  isUserAddedAttachment,
  isServerAsset,
  MAX_FILE_COUNT,
  AttachmentReducerState
} from './attachmentReducer'
import {
  Text,
  GreyGrey40,
  GreyGrey80,
  Primary13SemiBold,
  Button,
  Tooltip,
  IconPlus,
  Box
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { NewAttachment } from './Attachment/NewAttachment'
import { ExistingAttachment } from './Attachment/ExistingAttachment'

export const AttachmentList: FC<{
  state: AttachmentReducerState
  dispatch: React.Dispatch<AttachmentReducerAction>
  onClickAddAttachment: () => void
  disableModifyAttachments?: false | { reason: string }
  maxFileSize?: number
}> = ({
  state,
  dispatch,
  onClickAddAttachment,
  disableModifyAttachments = false,
  maxFileSize
}) => {
  const maxFileCountReached =
    (state.attachments?.length ?? 0) === MAX_FILE_COUNT
  const addDisabled = maxFileCountReached || !!disableModifyAttachments

  const handleUploadSuccess = useCallback(
    (asset: Asset, clientId: string) => {
      dispatch({
        type: 'UPLOAD_SUCCESS',
        clientId,
        asset
      })
    },
    [dispatch]
  )

  return (
    <div>
      <Box mb={2} position="relative">
        <Text {...Primary13SemiBold} color={GreyGrey80}>
          Attachments
        </Text>
        <Box position="absolute" right={0} top={-1.5}>
          <Tooltip
            isDisabled={!addDisabled}
            label={
              disableModifyAttachments
                ? disableModifyAttachments?.reason
                : 'Maximum number of attachments reached'
            }
            placement="top"
          >
            <Button
              variant="ghost"
              disabled={addDisabled}
              icon={<IconPlus color={addDisabled ? GreyGrey40 : 'black'} />}
              aria-label="add attachment"
              size="24px"
              onClick={onClickAddAttachment}
            />
          </Tooltip>
        </Box>
      </Box>
      {state.attachments?.map(attachment =>
        match(attachment)
          .when(isUserAddedAttachment, userAddedAttachment => (
            <NewAttachment
              maxFileSize={maxFileSize}
              key={userAddedAttachment.clientId}
              clientId={userAddedAttachment.clientId}
              file={userAddedAttachment.file}
              onUploadSuccess={handleUploadSuccess}
              onClickDelete={() => {
                dispatch({
                  type: 'DELETE_ATTACHMENT',
                  attachment: userAddedAttachment
                })
              }}
            />
          ))
          .when(isServerAsset, serverAsset => (
            <ExistingAttachment
              key={serverAsset.id}
              attachment={serverAsset}
              disableModifyAttachments={disableModifyAttachments}
              onClickDelete={() =>
                dispatch({
                  type: 'DELETE_ATTACHMENT',
                  attachment: serverAsset
                })
              }
              onClickPreview={() =>
                dispatch({
                  type: 'PREVIEW_ATTACHMENT',
                  attachment: serverAsset
                })
              }
            />
          ))
          .exhaustive()
      )}
    </div>
  )
}
